import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Grid, Typography, Box, Paper, Stack, Chip } from '@mui/material';
import {
  School as SchoolIcon,
  EmojiEvents as EmojiEventsIcon,
  // Forum as ForumIcon,
  TrendingUp as TrendingUpIcon,
  AutoStories as AutoStoriesIcon,
  EmojiObjects as EmojiObjectsIcon,
  Groups as GroupsIcon,
  AccountCircle as AccountCircleIcon,
  MenuBook as MenuBookIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UIKit/Button';
import Card from '../components/UIKit/Card';
import { useTranslation } from 'react-i18next';

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation('homepage');

  const features = [
    {
      title: t('features.courses.title'),
      description: t('features.courses.description'),
      icon: <SchoolIcon sx={{ fontSize: 60 }} />,
      path: '/courses',
    },
    {
      title: t('features.leaderboard.title'),
      description: t('features.leaderboard.description'),
      icon: <EmojiEventsIcon sx={{ fontSize: 60 }} />,
      path: '/leaderboard',
    },
    // TODO: Forum feature temporarily hidden, remove forum access from homepage
    // {
    //   title: t('features.forum.title'),
    //   description: t('features.forum.description'),
    //   icon: <ForumIcon sx={{ fontSize: 60 }} />,
    //   path: '/forum',
    // },
    {
      title: t('features.analytics.title'),
      description: t('features.analytics.description'),
      icon: <TrendingUpIcon sx={{ fontSize: 60 }} />,
      path: '/dashboard',
    },
  ];

  const benefits = [
    {
      title: t('benefits.quality.title'),
      description: t('benefits.quality.description'),
      icon: <AutoStoriesIcon sx={{ fontSize: 40 }} />,
    },
    {
      title: t('benefits.gamification.title'),
      description: t('benefits.gamification.description'),
      icon: <EmojiObjectsIcon sx={{ fontSize: 40 }} />,
    },
    {
      title: t('benefits.social.title'),
      description: t('benefits.social.description'),
      icon: <GroupsIcon sx={{ fontSize: 40 }} />,
    },
  ];

  return (
    <Container>
      <Box
        sx={{
          mt: { xs: 4, sm: 6, md: 8 },
          mb: { xs: 3, sm: 4, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          component="h1"
          variant="h2"
          align="center"
          color="primary"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            lineHeight: 1.2,
          }}
        >
          {t('welcome')}
        </Typography>
        <Typography
          variant="h5"
          align="center"
          color="text.secondary"
          paragraph
          sx={{
            maxWidth: '800px',
            mb: { xs: 3, sm: 4 },
            fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
            px: { xs: 1, sm: 2 },
          }}
        >
          {t('description')}
        </Typography>
        {!isAuthenticated && (
          <Box
            sx={{
              mt: { xs: 2, sm: 3, md: 4 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1.5, sm: 2 },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              fullWidth={true}
              sx={{ minWidth: { sm: '200px' } }}
            >
              {t('buttons.start_learning')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              fullWidth={true}
              sx={{ minWidth: { sm: '200px' } }}
            >
              {t('buttons.login')}
            </Button>
          </Box>
        )}
      </Box>

      {/* Onboarding banner for authenticated users */}
      {isAuthenticated && (
        <Paper
          elevation={0}
          sx={{
            mb: { xs: 4, sm: 6 },
            p: { xs: 2.5, sm: 4 },
            borderRadius: '20px',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(25,118,210,0.18) 0%, rgba(100,181,246,0.10) 100%)'
                : 'linear-gradient(135deg, rgba(227,242,253,1) 0%, rgba(187,222,251,0.5) 100%)',
            border: '1px solid',
            borderColor: 'primary.light',
          }}
        >
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label={t('banner.badge')}
                color="primary"
                size="small"
                sx={{ mb: 1.5, fontWeight: 600 }}
              />
              <Typography
                variant="h5"
                fontWeight={700}
                gutterBottom
                sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' } }}
              >
                {t('banner.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
                {t('banner.description')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AccountCircleIcon />}
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/profile')}
                >
                  {t('banner.goToProfile')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<MenuBookIcon />}
                  onClick={() => navigate('/learning-paths')}
                >
                  {t('banner.browsePaths')}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Stack spacing={1}>
                {[
                  t('banner.steps.0'),
                  t('banner.steps.1'),
                  t('banner.steps.2'),
                ].map((step, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleOutlineIcon color="primary" sx={{ flexShrink: 0 }} />
                    <Typography variant="body2" color="text.secondary">
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {features.map((feature) => (
          <Grid item key={feature.title} xs={12} sm={6} xl={4}>
            <Card
              clickable
              component={Link}
              to={feature.path}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                p: { xs: 2, sm: 2.5, md: 3 },
                textDecoration: 'none',
              }}
            >
              <Box
                sx={{
                  color: 'primary.main',
                  mb: { xs: 1.5, sm: 2 },
                  '& > svg': {
                    fontSize: { xs: 40, sm: 50, md: 60 },
                  },
                }}
              >
                {feature.icon}
              </Box>
              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' },
                  mb: { xs: 1, sm: 1.5 },
                }}
              >
                {feature.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                }}
              >
                {feature.description}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          mt: { xs: 4, sm: 6, md: 8 },
          mb: { xs: 3, sm: 4 },
          textAlign: 'center',
          px: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            mb: { xs: 2, sm: 3 },
          }}
        >
          {t('why_choose_us')}
        </Typography>
        <Grid
          container
          spacing={{ xs: 2, sm: 3, md: 4 }}
          sx={{ mt: { xs: 1, sm: 2 } }}
        >
          {benefits.map((benefit) => (
            <Grid item key={benefit.title} xs={12} md={4}>
              <Card
                sx={{
                  p: { xs: 2, sm: 2.5, md: 3 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    color: 'primary.main',
                    mb: { xs: 1, sm: 1.5 },
                    '& > svg': {
                      fontSize: { xs: 32, sm: 36, md: 40 },
                    },
                  }}
                >
                  {benefit.icon}
                </Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' },
                    mb: { xs: 1, sm: 1.5 },
                  }}
                >
                  {benefit.title}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                  }}
                >
                  {benefit.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default Home;
